/**
 * A convenience generic function for calling a hey-api / OpenAPI SDK function.
 *
 * What this solves:
 * - Strongly types the returned data from SDK functions that resolve to Promise<{ data?: T }>.
 * - Default behavior returns a non-null, non-undefined T (throws if empty).
 * - Can be configured to allow null/undefined via { allowEmpty: true }.
 *
 * Supported call patterns:
 * - Zero-argument SDK calls: callApi(vendorApi.getUsers)
 * - Single-argument SDK calls: callApi(vendorApi.getIssueScoregaps, { path: { issue_id: 123 } })
 * - Optional allowEmpty flag:
 *     - callApi(vendorApi.getUsers, { allowEmpty: true })
 *     - callApi(vendorApi.getIssueScoregaps, { path: { issue_id: 123 } }, { allowEmpty: true })
 *
 * Examples:
 *   // Returns ScoreGaps[] (throws if response.data is null/undefined)
 *   const gaps = await callApi(vendorApi.getIssueScoregaps, {
 *     path: { issue_id: 123 },
 *   })
 *
 *   // Returns ScoreGaps[] | null | undefined (does not throw on empty)
 *   const maybeGaps = await callApi(
 *     vendorApi.getIssueScoregaps,
 *     { path: { issue_id: 123 } },
 *     { allowEmpty: true }
 *   )
 */

/* ---------------------------------------------
   Helper types
   --------------------------------------------- */

/**
 * The minimal response shape hey-api SDK functions return.
 * We only care about the optional `data` property for typing.
 */
export type ApiResponseLike = { data?: unknown }

/**
 * Extract the non-null data type T from a Promise<{ data?: T }>.
 * - NonNullable ensures the default call signature returns a non-empty type.
 */
export type ApiDataFrom<P> =
  P extends Promise<infer R>
    ? R extends { data?: infer D }
      ? NonNullable<D>
      : never
    : never

/** Option flag to allow null/undefined data to be returned instead of throwing. */
export type AllowEmptyOption = { allowEmpty: true }

/** A convenience alias for "T or empty". */
export type MaybeEmpty<T> = T | null | undefined

/* ---------------------------------------------
   Overloads
   --------------------------------------------- */

/** Zero-argument API function (throws on empty by default). */
export async function callApi<F extends () => Promise<ApiResponseLike>>(
  apiFunction: F
): Promise<ApiDataFrom<ReturnType<F>>>

/** Zero-argument API function (allows empty). */
export async function callApi<F extends () => Promise<ApiResponseLike>>(
  apiFunction: F,
  options: AllowEmptyOption
): Promise<MaybeEmpty<ApiDataFrom<ReturnType<F>>>>

/** Single-argument API function (throws on empty by default). */
export async function callApi<
  A,
  F extends (arg: A) => Promise<ApiResponseLike>,
>(apiFunction: F, apiArg: A): Promise<ApiDataFrom<ReturnType<F>>>

/** Single-argument API function (allows empty). */
export async function callApi<
  A,
  F extends (arg: A) => Promise<ApiResponseLike>,
>(
  apiFunction: F,
  apiArg: A,
  options: AllowEmptyOption
): Promise<MaybeEmpty<ApiDataFrom<ReturnType<F>>>>

/* ---------------------------------------------
   Implementation
   --------------------------------------------- */

/**
 * Unified implementation for all overloads above.
 * Internal signature returns MaybeEmpty<T>, while the overloads ensure
 * a non-empty T is inferred for the default (no allowEmpty) cases.
 */
export async function callApi<
  F extends (arg?: unknown) => Promise<ApiResponseLike>,
>(
  apiFunction: F,
  // Either the API's options arg OR an AllowEmptyOption when calling a zero-arg API.
  argOrOptions?: Parameters<F>[0] | AllowEmptyOption,
  // Explicit AllowEmptyOption when also providing the API options arg.
  maybeOptions?: { allowEmpty?: boolean }
): Promise<MaybeEmpty<ApiDataFrom<ReturnType<F>>>> {
  // Detect the "zero-arg + allowEmpty as 2nd param" form:
  // callApi(apiFn, { allowEmpty: true })
  const hasOnlyAllowOption =
    typeof argOrOptions === 'object' &&
    argOrOptions !== null &&
    'allowEmpty' in (argOrOptions as Record<string, unknown>) &&
    maybeOptions === undefined

  // Resolve allowEmpty flag regardless of call shape
  const allowEmpty =
    (hasOnlyAllowOption
      ? (argOrOptions as { allowEmpty?: boolean }).allowEmpty
      : maybeOptions?.allowEmpty) ?? false

  // Invoke the SDK function in the correct shape:
  // - zero-arg, or
  // - single-arg (options object)
  const response =
    hasOnlyAllowOption || argOrOptions === undefined
      ? await apiFunction()
      : await apiFunction(argOrOptions as Parameters<F>[0])

  // Narrow response.data to the inferred data type (or empty)
  const data =
    (response.data as ApiDataFrom<ReturnType<F>> | null | undefined) ??
    undefined

  // Default behavior: enforce non-empty
  if (data == null && !allowEmpty) {
    throw new Error('API response is empty')
  }
  return data
}
